<script setup lang="ts">
import type { IPlayerState } from "@pixellot/vue-player/src/util/state";
import type { VideoJsPlayer as IPixellotPlayer } from "@pixellot/web-sdk";
import { VideoClipper } from "@pixellot/vue-player";
import { SEGMENT_TRACK_EVENTS } from "~/constants/segment";
import useAsync from "~/hooks/use-async";
import EventsAPI from "~/services/events";
import useEventStore from "~/stores/event";
import getErrorLocalePathMessage from "~/utils/locale";
import { formatTime } from "~/utils/time";

export interface CreateClipModalProps {
  mediaItem?: IModelEvent | null;
}

const props = defineProps<CreateClipModalProps>();

const toaster = useToast();
const { t, te } = useI18n();
const segment = useSegment();
const eventStore = useEventStore();
const spriteOptions = useSpriteThumbnails();
const isEditingClip = computed(() => eventStore.clips.isEditingClip);

const clipData = reactive({
  name: isEditingClip.value ? props.mediaItem?.title : "",
  targetId: props.mediaItem?.referenceId,
  url: props.mediaItem?.source.hd || "",
  startTime: 0,
  endTime: 0,
  streamName: "hd",
} as IRequestPayloadClipData);

const closeModal = () => {
  eventStore.clips.isCreatingClip = false;
  eventStore.clips.isEditingClip = false;
};
const onError = (error: Error) => {
  const { message } = getErrorLocalePathMessage(error.message, { t, te });
  return toaster.error(message) && closeModal();
};
const onSuccess = () => toaster.success(isEditingClip.value
  ? t("pages.events.tabs.event_videos.clip_updating_successfully_text")
  : t("pages.events.tabs.event_videos.clip_creating_successfully_text"),
) && closeModal();

const { loading: creatingClip, run: createClip } = useAsync(EventsAPI.userMedia.createUserClip, { onSuccess, onError });
const { loading: updatingClip, run: updateClip } = useAsync(EventsAPI.userMedia.updateUserClip, { onSuccess, onError });
const loading = computed(() => creatingClip.value || updatingClip.value);

const onFormSubmit = () => {
  if (isEditingClip.value) {
    updateClip(props.mediaItem?.id, clipData.name).then(() => {
      if (eventStore.data)
        eventStore.data.title = clipData.name;
      const userClip = eventStore.userClips.find(clip => clip.id === props.mediaItem?.id);

      if (userClip)
        userClip.title = clipData.name;
    });
  }
  else {
    createClip(clipData);
    if (props.mediaItem?.id) {
      segment.track(SEGMENT_TRACK_EVENTS.CLIP_CREATED, { event_id: props.mediaItem.id });
    }
  }
};
const player = computed<IPixellotPlayer | null>(() => eventStore.player ? eventStore.player : null);
const playerState = computed<IPlayerState | null>(() => eventStore.playerState ? eventStore.playerState : null);
const videoClipperRef = ref<InstanceType<typeof VideoClipper> | null>(null);

const selectedDuration = computed(() => {
  if (!videoClipperRef.value || !videoClipperRef.value.selectedRange || !eventStore.player)
    return 0;

  const [rangeStart, rangeEnd] = videoClipperRef.value.selectedRange;
  const duration = rangeEnd - rangeStart;

  return Math.ceil(duration);
});

watch(() => videoClipperRef.value?.selectedRange, (range) => {
  if (!range)
    return;

  clipData.startTime = Math.floor(range[0]);
  clipData.endTime = Math.floor(range[1]);
});

onMounted(() => {
  const currentTime = eventStore.player?.currentTime() || 0;
  const timeMargin = 60;
  const start = currentTime - timeMargin;
  const end = currentTime + timeMargin;

  nextTick(() => {
    if (player.value) {
      player.value?.trimAPI.setRange(start, end);
      videoClipperRef.value?.setRange(currentTime, end);

      player.value.spriteThumbnails().updateOptions(spriteOptions.value);
    }
  });
});
onBeforeUnmount(() => eventStore.player?.trimAPI?.hide());
</script>

<template>
  <div class="createClipModal">
    <ClientOnly>
      <Teleport to="#player-bottom">
        <div v-if="eventStore.tags.isCreatingTag || eventStore.clips.isCreatingClip" class="flex flex-col background-main">
          <div class="flex items-center justify-center gap-4 my-4 text-secondary">
            <div class="border min-w-[5rem] px-4 py-1 rounded border-[var(--base-card-bgSecondaryColor)]">
              {{ formatTime(clipData.startTime) }}
            </div>
            <span>-</span>
            <div class="border min-w-[5rem] px-4 py-1 rounded border-[var(--base-card-bgSecondaryColor)]">
              {{ formatTime(clipData.endTime) }}
            </div>
          </div>
          <VideoClipper
            v-if="player && playerState"
            ref="videoClipperRef"
            :player="player"
            :player-state="playerState"
          />

          <div class="my-4 text-sm font-medium text-center text-secondary">
            {{ selectedDuration }} {{ t('labels.seconds', 0) }}
          </div>
        </div>
      </Teleport>
    </ClientOnly>

    <div class="p-4 createClipModal__container">
      <div class="flex items-center justify-between">
        <h2 class="text-[var(--base-tab-text-color)] text-heading-3 text-center">
          {{ isEditingClip ? t('pages.events.tabs.event_videos.rename_clip') : t("pages.events.tabs.event_videos.create_clip") }}
        </h2>

        <BaseButton
          type="icon"
          name="modal-close"
          :title="t('button.cancel')"
          @click.prevent="closeModal()"
        >
          <IconClose />
        </BaseButton>
      </div>

      <form @submit.prevent="onFormSubmit">
        <BaseInput
          v-model="clipData.name"
          :label="t('pages.events.tabs.event_videos.name_the_clip')"
          label-class="text-heading-4"
          :placeholder="t('labels.clip_name_placeholder')"
          type="text"
          name="clip-modal"
          root-class="mt-6"
          required
          outline
        />
        <div class="flex justify-between w-full gap-2 mt-6">
          <BaseButton
            type="secondary"
            name="modal-close"
            :title="t('button.cancel')"
            class="w-1/2 text-button-bold text-main"
            @click="closeModal()"
          >
            {{ t("button.cancel") }}
          </BaseButton>
          <BaseButton
            type="primary"
            name="submit form"
            :title="t('pages.events.tabs.event_videos.create_clip')"
            :disabled="loading"
            class="w-1/2 text-button-bold text-main"
          >
            <div class="flex items-center justify-center truncate">
              <IconClip :width="16" :height="16" fill="#ffffff" class="flex-shrink-0 mx-2" />
              {{ isEditingClip ? t('pages.events.tabs.event_videos.rename_clip') : t("pages.events.tabs.event_videos.create_clip") }}
            </div>
          </BaseButton>
        </div>
      </form>
    </div>
  </div>
</template>

<style lang="scss">
.createClipModal {
  @apply w-full h-full;

  .createClipModal__container {
    @apply h-full;

    background: var(--base-card-bgColor);
  }
}
</style>
